import React from 'react'

import clsx from 'clsx'

import ClickoutWrapper from 'components/ClickoutWrapper'
import CtaButton from 'components/CtaButton'
import FeatureCheck from 'components/FeatureCheck'
import Grid from 'components/Grid'
import Icon from 'components/Icon'
import Link from 'components/Link'
import Save from 'components/Listing/components/Buttons/components/Save'
import { FixedImage } from 'components/NextImage'
import RatingDisplay from 'components/RatingDisplay'
import BreakdownDisplay from 'components/Review/components/BreakdownDisplay'
import Skeleton from 'components/Skeleton'
import { EventContext, useEventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'

import useAppFetchContext, { STATUS } from '../../useAppFetchContext'

import classes from './ContentProduct.module.scss'

const ContentProduct = ({ slug }) => {
  const { is } = useViewport()

  const { status, products } = useAppFetchContext()
  const eventContext = useEventContext({
    engagementComponent: 'product_card-item',
  })

  const product = products[slug]
  const isChecked = feature => product.pricing.categories.includes(feature)

  if (status === STATUS.error) return null
  if (status === STATUS.initial || status === STATUS.loading)
    return <Skeleton variant="rect" className={clsx('ContentProduct', classes.loadingTemplate)} />

  return (
    <EventContext value={{ engagementComponent: 'product_card-item' }}>
      {status === STATUS.success && product && (
        <div className={clsx('ContentProduct', classes.root)}>
          <div className={classes.header}>
            <Grid container className={classes.container}>
              <Grid item xs={12} md={6} className={classes.logoWithRatingContainer}>
                <div className={classes.logoContainer}>
                  <a
                    href={product.cta_urls.detail}
                    {...eventContext.addEvent({
                      name: 'engagement_product_click',
                      engagementDestination: 'go-to_product_details',
                      engagementDetails: 'logo_product',
                    })}
                  >
                    <FixedImage
                      className={classes.logo}
                      width={72}
                      src={product.logo_url}
                      alt={`${product.name} logo`}
                    />
                  </a>
                </div>
                <div className={classes.nameContainer}>
                  <span className={classes.name}>{product.name}</span>
                  <Link
                    className={classes.learnMore}
                    href={product.cta_urls.detail}
                    variant="body-xs"
                    {...eventContext.addEvent({
                      name: 'engagement_product_click',
                      engagementDestination: 'go-to_product_details',
                      engagementDetails: 'text-link_learn-more-about-product',
                    })}
                  >
                    Learn more about {product.name}
                  </Link>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} className={classes.ctaButtonContainer}>
                <div>
                  <ClickoutWrapper
                    isClickout={product.is_premium}
                    className={classes.ctaButton}
                    url={product.is_premium ? product.cta_urls.cta : product.cta_urls.detail}
                    {...eventContext.addEvent({
                      engagementComponent: 'product_card',
                      name: product.is_premium ? 'engagement_product_clickout' : 'engagement_product_clickout',
                      engagementDestination: `go-to_${product.is_premium ? 'vendor-website' : 'product_details'}`,
                      engagementDetails: `button_${product.is_premium ? 'visit-website' : 'learn-more'}`,
                    })}
                  >
                    <CtaButton color="primary" fullWidth>
                      {product.is_premium ? (
                        <>
                          visit website
                          <Icon
                            className={classes.ctaButtonExternalLinkIcon}
                            icon={['far', 'external-link']}
                            size="16"
                          />
                        </>
                      ) : (
                        <>learn more</>
                      )}
                    </CtaButton>
                  </ClickoutWrapper>
                  <Save className={classes.saveButton} listing={product} onlyIcon={is('xs')} />
                </div>
              </Grid>
            </Grid>
          </div>
          <div className={classes.accordionContent}>
            <Grid container className={classes.contentContainer}>
              <Grid container item xs={12} sm={6} lg={4}>
                <Grid item xs={12} className={classes.featuresLogoTitle}>
                  <div className={classes.featuresLogo}>
                    <img alt={`${product.name} logo`} src={product.logo_url} />
                  </div>
                  <span className={classes.featuresTitle}>{product.name} features</span>
                </Grid>
                <Grid item xs={12}>
                  <ul className={classes.features}>
                    {product.features.popular.slice(0, 4).map(feature => (
                      <li className={classes.feature} key={feature.slug}>
                        {feature.name}
                      </li>
                    ))}
                  </ul>
                </Grid>
              </Grid>
              <Grid container item xs={12} sm={6} lg={3} className={classes.pricingOptions}>
                <Grid item xs={12}>
                  <span className={classes.featuresTitle}>Pricing options</span>
                </Grid>
                <Grid item xs={12} className={classes.featuresCheckContainer}>
                  <FeatureCheck checked={isChecked('free')}>Free plan</FeatureCheck>
                  <FeatureCheck checked={isChecked('subscription')}>Subscription</FeatureCheck>
                  <FeatureCheck checked={isChecked('free-trial')}>Free trial</FeatureCheck>
                </Grid>
              </Grid>
              <Grid item container xs={12} lg={5}>
                <Grid item xs={12}>
                  <span className={classes.featuresTitle}>Overall rating</span>
                </Grid>
                <Grid item xs={4} sm={3} md={2} lg={4} className={classes.ratingDisplayContainer}>
                  <RatingDisplay
                    href={product.cta_urls.reviews}
                    rating={product.rating_avg}
                    totalNumberOfReviews={product.num_reviews}
                    hasDenominator={false}
                    enableLink={false}
                  />
                </Grid>
                <Grid item xs={8} sm={9} md={7} lg={8} className={classes.ratingContainer}>
                  <BreakdownDisplay reviewsByRating={product.reviews.by_rating} />
                </Grid>
              </Grid>
            </Grid>
          </div>
        </div>
      )}
    </EventContext>
  )
}

export default ContentProduct
